import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ServerPaginatedTable from './ServerPaginatedTable';
import Spinner from './Spinner';
import { useAuth } from './AuthContext';
import AddCardGroupModal from './AddCardGroupModal';
import { useTheme } from './ThemeContext';
import MobileHeader from './MobileHeader';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setCardGroupData } from './cardGroupSlice';
import { fetchCardGroupsPaginated, invalidateCardGroupsCache } from './policiesSlice';
import PageHeader from './PageHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@fortawesome/free-solid-svg-icons';

const CardGroupManager = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const primaryColor = theme.primary;
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();

    // Redux state
    const {
        data: cardGroups,
        pagination,
        loading,
        error: reduxError
    } = useSelector((state) => state.policies.paginatedCardGroups);

    // URL-based state with defaults
    const currentPage = parseInt(searchParams.get('page')) || 1;
    const pageSize = parseInt(searchParams.get('pageSize')) || 50;
    const sortField = searchParams.get('sortField') || 'cardGroupID';
    const sortDirection = searchParams.get('sortDirection') || 'ASC';
    const serverSearch = searchParams.get('search') || '';
    const accountFilter = searchParams.get('accountFilter') || '';
    const balanceFilter = searchParams.get('balanceFilter') || '';

    // Local UI state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [searchQuery, setSearchQuery] = useState(serverSearch);

    // Helper function to update URL parameters
    const updateSearchParams = useCallback((updates) => {
        setSearchParams(prevParams => {
            const newParams = new URLSearchParams(prevParams);

            Object.entries(updates).forEach(([key, value]) => {
                if (value === undefined || value === null || value === '') {
                    newParams.delete(key);
                } else {
                    newParams.set(key, value.toString());
                }
            });

            // Remove default values to keep URL clean
            if (newParams.get('page') === '1') newParams.delete('page');
            if (newParams.get('pageSize') === '50') newParams.delete('pageSize');
            if (newParams.get('sortField') === 'cardGroupID') newParams.delete('sortField');
            if (newParams.get('sortDirection') === 'ASC') newParams.delete('sortDirection');

            return newParams;
        });
    }, [setSearchParams]);

    // Memoized fetch function
    const fetchData = useCallback((params = {}) => {
        const fetchParams = {
            userId: currentUser?.userId,
            page: params.page || currentPage,
            pageSize: params.pageSize || pageSize,
            sortField: params.sortField || sortField,
            sortDirection: params.sortDirection || sortDirection,
            search: params.search !== undefined ? params.search : serverSearch,
            accountFilter: params.accountFilter || accountFilter,
            balanceFilter: params.balanceFilter || balanceFilter,
            forceRefresh: params.forceRefresh || false
        };

        console.log('📡 CARDGROUP FETCH REQUEST:', {
            fetchParams,
            urlParams: { sortField, sortDirection, currentPage, pageSize },
            overrideParams: params
        });

        if (fetchParams.userId) {
            dispatch(fetchCardGroupsPaginated(fetchParams));
        }
    }, [
        dispatch,
        currentUser?.userId,
        currentPage,
        pageSize,
        sortField,
        sortDirection,
        serverSearch,
        accountFilter,
        balanceFilter
    ]);

    // Debounced search effect
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (serverSearch !== searchQuery) {
                updateSearchParams({
                    search: searchQuery,
                    page: 1 // Reset to page 1 when searching
                });
            }
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchQuery, serverSearch, updateSearchParams]);

    // Sync searchQuery with URL parameter
    useEffect(() => {
        setSearchQuery(serverSearch);
    }, [serverSearch]);

    // Fetch data when dependencies change
    useEffect(() => {
        fetchData();
    }, [fetchData]);


    const handleSort = (field, direction) => {
        console.log('🔄 CARDGROUP SORT TRIGGERED:', { field, direction, from: 'handleSort' });
        updateSearchParams({
            sortField: field,
            sortDirection: direction,
            page: 1 // Reset to page 1 when sorting
        });
    };

    const handlePageChange = (newPage) => {
        updateSearchParams({ page: newPage });
    };

    const handlePageSizeChange = (newPageSize) => {
        updateSearchParams({
            pageSize: newPageSize,
            page: 1 // Reset to page 1 when changing page size
        });
    };

    const handleFilterChange = (filterType, value) => {
        const updates = { page: 1 };

        switch (filterType) {
            case 'account':
                updates.accountFilter = value;
                break;
            case 'balance':
                updates.balanceFilter = value;
                break;
        }

        updateSearchParams(updates);
    };

    const handleEditClick = (rowData) => {
        dispatch(setCardGroupData(rowData));
        navigate('/editcardgroup');
    };

    const handleAddCardGroup = (newGroup) => {
        if (newGroup.cardGroup) {
            setSuccessMessage("Successfully added the Account!");
            // Invalidate cache and refetch current page
            dispatch(invalidateCardGroupsCache());
            fetchData({ forceRefresh: true });
        } else {
            setSuccessMessage("Error adding account. Try Again.");
        }
    };

    const clearSuccessMessage = () => setSuccessMessage("");

    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage('');
            }, 7000);

            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    const columns = useMemo(() => [
        {
            name: 'Account #',
            selector: row => row.cardGroup.AccountNumber,
            sortable: true,
            sortField: 'AccountNumber',
            reorder: true
        },
        {
            name: 'Account Name',
            selector: row => row.cardGroup.groupName,
            sortable: true,
            sortField: 'groupName',
            reorder: true
        },
        {
            name: 'Description',
            selector: row => row.cardGroup.description,
            sortable: true,
            sortField: 'description',
            reorder: true
        },
        {
            name: 'Balance',
            selector: row => `$${row.cardGroup.AccountBalance}`,
            sortable: true,
            sortField: 'AccountBalance',
            reorder: true
        },
        {
            name: 'Edit',
            cell: row => (
                <button
                    className="themed-button"
                    onClick={() => handleEditClick(row)}
                >
                    Edit
                </button>
            ),
            ignoreRowClick: true,
        }
    ], []);

    const totalPages = pagination?.totalPages || 0;
    const totalCount = pagination?.totalCount || 0;

    return (
        <>
            <MobileHeader theme={theme} />
            <div className='mobile-container'>
                <PageHeader
                    heading="Accounts"
                    context="Create and edit your accounts."
                />
                {reduxError && <div className="alert alert-danger">{reduxError}</div>}

                {/* Advanced Filters Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4 p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters & Search</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search accounts, names, numbers..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                            {loading && searchQuery && (
                                <div className="text-xs text-gray-500 mt-1">Searching...</div>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Account Filter</label>
                            <input
                                type="text"
                                value={accountFilter}
                                onChange={(e) => handleFilterChange('account', e.target.value)}
                                placeholder="Filter by account number"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Balance Filter</label>
                            <select
                                value={balanceFilter}
                                onChange={(e) => handleFilterChange('balance', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="">All Balances</option>
                                <option value="positive">Positive Balance</option>
                                <option value="negative">Negative Balance</option>
                                <option value="zero">Zero Balance</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Results Summary */}
                {pagination && (
                    <div className="bg-gray-50 rounded-lg p-3 mb-4 flex justify-between items-center">
                        <div className="text-sm text-gray-600">
                            Total: {totalCount} accounts
                        </div>
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => fetchData({ forceRefresh: true })}
                                className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center space-x-1"
                                disabled={loading}
                                title="Refresh data"
                            >
                                <FontAwesomeIcon
                                    icon={faSync}
                                    className={`text-xs ${loading ? 'animate-spin' : ''}`}
                                />
                                <span>Refresh</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* Server-Side Paginated Table */}
                <ServerPaginatedTable
                    columns={columns}
                    data={cardGroups || []}
                    primaryColor={primaryColor}
                    loading={loading}
                    alertMessage={successMessage}
                    clearAlert={clearSuccessMessage}
                    pagination={pagination}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    onSort={handleSort}
                    pageSizeOptions={[25, 50, 100]}
                    children={
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="themed-button rounded-md px-4 py-2"
                            disabled={loading}
                        >
                            Create New Account
                        </button>
                    }
                />


                {isModalOpen && (
                    <AddCardGroupModal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        onSave={handleAddCardGroup}
                    />
                )}
            </div>
        </>
    );
};

export default CardGroupManager;